/**
 * Detects whether a formula is a text template (contains bare text outside
 * field refs) vs a pure numeric/formula expression.
 *
 * Ported from superturbodb_server/modules/formula-runner.js `isTemplateFormula`.
 *
 * Examples:
 *   "{price} + {tax}"             → false (numeric expression)
 *   "{formula} - 2"               → false (numeric expression)
 *   "SUM({a}, {b})"               → false (function call)
 *   "{name} horses!"              → true  (text template)
 *   "{grandparent.child.number} horses!" → true (text template)
 */

const TOKEN_TYPES = {
  NUMBER: 'NUMBER',
  STRING: 'STRING',
  BOOLEAN: 'BOOLEAN',
  IDENT: 'IDENT',
  LPAREN: 'LPAREN',
  RPAREN: 'RPAREN',
  COMMA: 'COMMA',
  OP: 'OP'
}

function tokenize (input) {

  const tokens = []
  let i = 0

  while (i < input.length) {

    const ch = input[i]

    if (/\s/.test(ch)) { i++; continue }

    if (ch === '(') { tokens.push({ type: TOKEN_TYPES.LPAREN, value: '(' }); i++; continue }
    if (ch === ')') { tokens.push({ type: TOKEN_TYPES.RPAREN, value: ')' }); i++; continue }
    if (ch === ',') { tokens.push({ type: TOKEN_TYPES.COMMA, value: ',' }); i++; continue }

    // Two-char operators
    if (i + 1 < input.length) {
      const two = input.substring(i, i + 2)
      if (two === '<>' || two === '>=' || two === '<=') {
        tokens.push({ type: TOKEN_TYPES.OP, value: two })
        i += 2
        continue
      }
    }

    if ('+-*/><=%^&'.includes(ch)) {
      tokens.push({ type: TOKEN_TYPES.OP, value: ch })
      i++
      continue
    }

    // String literal
    if (ch === '"') {
      let str = ''
      i++
      while (i < input.length && input[i] !== '"') {
        if (input[i] === '\\' && i + 1 < input.length) {
          str += input[i + 1]
          i += 2
        } else {
          str += input[i]
          i++
        }
      }
      i++ // closing quote
      tokens.push({ type: TOKEN_TYPES.STRING, value: str })
      continue
    }

    // Number (including decimals)
    if (/[0-9.]/.test(ch)) {
      let num = ''
      while (i < input.length && /[0-9.eE]/.test(input[i])) {
        num += input[i]
        i++
      }
      tokens.push({ type: TOKEN_TYPES.NUMBER, value: parseFloat(num) })
      continue
    }

    // Identifier (function name or boolean)
    if (/[A-Za-z_]/.test(ch)) {
      let ident = ''
      while (i < input.length && /[A-Za-z0-9_]/.test(input[i])) {
        ident += input[i]
        i++
      }
      if (ident === 'TRUE') {
        tokens.push({ type: TOKEN_TYPES.BOOLEAN, value: true })
      } else if (ident === 'FALSE') {
        tokens.push({ type: TOKEN_TYPES.BOOLEAN, value: false })
      } else {
        tokens.push({ type: TOKEN_TYPES.IDENT, value: ident })
      }
      continue
    }

    throw new Error(`Unexpected character: "${ch}"`)
  }

  return tokens
}

export function isTemplateFormula (formulaStr) {

  if (!formulaStr || !formulaStr.trim()) return false

  const withoutRefs = formulaStr.replace(/\{[^}]+\}/g, '0')

  try {

    const tokens = tokenize(withoutRefs)

    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type === TOKEN_TYPES.IDENT) {
        const next = tokens[i + 1]
        if (!next || next.type !== TOKEN_TYPES.LPAREN) return true
      }
    }

    return false
  } catch (e) {
    return true
  }
}

/**
 * Returns the inferred data type for a formula column's output.
 * 'number' if the formula is a pure expression, 'string' if it's a text template.
 */
export function inferFormulaResultType (formulaStr) {

  return isTemplateFormula(formulaStr) ? 'string' : 'number'
}
